import {
  Badge,
  Button,
  Divider,
  Group,
  Image,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';

import { AppShell } from '../../../components/app-shell/AppShell';
import { EmptyState, MotionSection } from '../../../components/ui';

/**
 * /host/venues/$id/preview — own-venue public preview.
 *
 * Renders the venue exactly as it will appear at /venues/$slug once live.
 * The /venues/$slug page belongs to wave 2c (browse-search); this preview
 * uses the same data source (/api/venues/me/venues/:id) so the host can
 * proof-read before submitting for review.
 */

type CalendarEntry = {
  id: string;
  entry_kind: 'block' | 'open_window';
  start_date: string;
  end_date: string;
};

type Photo = {
  id: string;
  url: string;
  alt_text: string;
  caption: string | null;
  position: number;
  is_hero: boolean;
};

type VenueDetail = {
  ok: true;
  venue: {
    id: string;
    name: string;
    slug: string;
    summary: string | null;
    description: string;
    capacity_min: number;
    capacity_max: number;
    address_line: string;
    city: string;
    region: string | null;
    country: string;
    address_visible_to_public: boolean;
    house_rules: string | null;
    stipend_offered: boolean;
    stipend_description: string | null;
    status: string;
    verified_at: number | null;
  };
  photos: Photo[];
  art_forms_accepted: string[];
  venue_types: string[];
  vibe_tags: string[];
  sound_tech: string[];
  sleeping_arrangements: string[];
  food_offered: string[];
  drink_offered: string[];
  facilities: string[];
  calendar_entries: CalendarEntry[];
};

type ApiError = { ok: false; error: { code: string; message: string } };

async function fetchVenue(id: string): Promise<VenueDetail | ApiError> {
  const r = await fetch(`/api/venues/me/venues/${encodeURIComponent(id)}`, {
    credentials: 'include',
  });
  return (await r.json()) as VenueDetail | ApiError;
}

function VenuePreview() {
  const { id } = Route.useParams();
  const q = useQuery({ queryKey: ['venue', id], queryFn: () => fetchVenue(id) });

  if (q.isPending) {
    return (
      <AppShell tone="content">
        <Loader />
      </AppShell>
    );
  }
  if (!q.data || !q.data.ok) {
    return (
      <AppShell tone="content">
        <EmptyState
          title="Couldn't load this venue."
          body={q.data && !q.data.ok ? q.data.error.message : 'Try again in a moment.'}
          action={
            <Button component={Link} to="/host/venues">
              Back to venues
            </Button>
          }
        />
      </AppShell>
    );
  }

  const detail = q.data;
  const venue = detail.venue;
  const hero = detail.photos.find((p) => p.is_hero) ?? detail.photos[0];
  const restPhotos = detail.photos.filter((p) => p.id !== hero?.id);

  return (
    <AppShell tone="content">
      <MotionSection>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={4}>
            <Title order={1}>{venue.name}</Title>
            {venue.summary !== null && (
              <Text size="lg" c="dimmed">
                {venue.summary}
              </Text>
            )}
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {[venue.city, venue.region, venue.country].filter(Boolean).join(', ')}
              </Text>
              <Badge variant="light">{venue.status}</Badge>
              {venue.verified_at !== null && <Badge color="brand">Verified</Badge>}
            </Group>
          </Stack>
          <Group>
            <Link
              to="/host/venues/$id/edit"
              params={{ id }}
              style={{ textDecoration: 'none' }}
            >
              <Button variant="light" component="span">
                Back to edit
              </Button>
            </Link>
          </Group>
        </Group>

        {hero && (
          <Image src={hero.url} alt={hero.alt_text} h={420} radius="md" fit="cover" />
        )}

        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Title order={3}>About</Title>
            <Text style={{ whiteSpace: 'pre-wrap' }}>{venue.description}</Text>
            <Group gap="xs">
              <Text fw={500}>Capacity:</Text>
              <Text>
                {venue.capacity_min}–{venue.capacity_max} people
              </Text>
            </Group>
          </Stack>
        </Paper>

        {restPhotos.length > 0 && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Title order={3}>Gallery</Title>
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                {restPhotos.map((p) => (
                  <Image
                    key={p.id}
                    src={p.url}
                    alt={p.alt_text}
                    h={160}
                    radius="sm"
                    fit="cover"
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </Paper>
        )}

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <ChipsCard title="Type" items={detail.venue_types} />
          <ChipsCard title="Art forms welcomed" items={detail.art_forms_accepted} />
          {detail.vibe_tags.length > 0 && (
            <ChipsCard title="Vibe" items={detail.vibe_tags} />
          )}
          {detail.sound_tech.length > 0 && (
            <ChipsCard title="Sound and tech" items={detail.sound_tech} />
          )}
          {detail.sleeping_arrangements.length > 0 && (
            <ChipsCard title="Sleeping" items={detail.sleeping_arrangements} />
          )}
          {detail.food_offered.length > 0 && (
            <ChipsCard title="Food" items={detail.food_offered} />
          )}
          {detail.drink_offered.length > 0 && (
            <ChipsCard title="Drink" items={detail.drink_offered} />
          )}
          {detail.facilities.length > 0 && (
            <ChipsCard title="Facilities" items={detail.facilities} />
          )}
        </SimpleGrid>

        {venue.house_rules !== null && venue.house_rules.length > 0 && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Title order={3}>House rules</Title>
              <Text style={{ whiteSpace: 'pre-wrap' }}>{venue.house_rules}</Text>
            </Stack>
          </Paper>
        )}

        {venue.stipend_offered && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Title order={3}>Stipend</Title>
              {venue.stipend_description !== null && venue.stipend_description.length > 0 ? (
                <Text>{venue.stipend_description}</Text>
              ) : (
                <Text c="dimmed">Stipend offered. Details discussed in the request thread.</Text>
              )}
            </Stack>
          </Paper>
        )}

        <Divider />
        <Text size="sm" c="dimmed">
          Address {venue.address_visible_to_public ? 'will be shown publicly' : 'is private until a request is confirmed'}: {venue.address_line}.
        </Text>
      </Stack>
      </MotionSection>
    </AppShell>
  );
}

function ChipsCard({ title, items }: { title: string; items: string[] }) {
  return (
    <Paper p="md" withBorder radius="md">
      <Stack gap="xs">
        <Title order={4}>{title}</Title>
        <Group gap="xs">
          {items.map((slug) => (
            <Badge key={slug} variant="outline">
              {prettySlug(slug)}
            </Badge>
          ))}
        </Group>
      </Stack>
    </Paper>
  );
}

function prettySlug(slug: string): string {
  return slug
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

export const Route = createFileRoute('/host/venues/$id/preview')({
  component: VenuePreview,
});
